<template>
	<div
	v-if="!hideStickySummary"
		class="sticky-summary bg-white"
		id="sticky-summary"
		style="display: none; opacity: 0"
		ref="stickySummary"
	>
		<div class="py-2 shadow-3">
			<div class="container d-flex d-lg-none flex-column mb-2">
				<span
					class="1"
					v-for="(prest, prest_i) in selectedPrestations"
					:key="prest_i"
				>
					<strong>{{ prest.quantity }} x </strong
					>{{ prest.prestation.prestName }}
				</span>
			</div>
			<div class="container d-flex justify-content-around">
				<div
					class="col-lg-7 d-none d-lg-flex flex-row nowrap align-items-center"
				>
					<div class="col-3 text-center px-0 heading text-grey-900">
						{{ content.planningSummary_yourSelection }}
					</div>
					<div
						class="prestations-list col-7 d-flex flex-column px-2 align-items-start"
					>
						<span
							class="text-start body text-grey-900"
							v-for="(prest, prest_i) in selectedPrestations"
							:key="prest_i"
						>
							{{ prest.quantity }} x {{ prest.prestation.prestName }}
						</span>
					</div>
					<div class="col-2 text-center px-0">
						<ParticipantsTotals
							:participants="participants"
							:content="content"
							:additionalClasses="'body text-grey-900'"
						/>
					</div>
				</div>
				<div
					class="col-12 col-lg-5 px-0 d-flex flex-row nowrap justify-content-between"
				>
					<div
						class="p-0 d-flex align-items-center justify-content-center justify-content-lg-end pe-2"
					>
						<!-- <Prices
							:isLoading="pricesLoadingSet.has(currentCacheKey)"
							:maxPromo="totals.maxPromo"
							:crossedPrice="totals.crossedPrice"
							:totalPrice="totals.totalPrice"
							:perPrice="totals.perPrice"
							:locale="locale"
						/> -->
						<component
							:is="checkIfPerPersonDisplayWebsite ? PricesPP : Prices"
							:isLoading="pricesLoadingSet.has(currentCacheKey)"
							:crossedPrice="totals.crossedPrice" 
							:totalPrice="totals.totalPrice" 
							:perPrice="totals.perPrice" 
							:maxPromo="totals.maxPromo"
							:locale="locale" 
							:totalParticipants="participants.length"
						></component>	
					</div>
					<div
						class="col-5 col-sm-6 ps-md-0 text-center d-flex align-items-center justify-content-center"
					>
						<div class="w-100 d-flex align-items-center justify-content-center">
							<button
								@click.stop="handleReserveClick"
								class="button-primary primary-normal w-100 d-flex flex-row nowrap align-items-center justify-content-center"
							>
								<span class="buttons-default px-3 px-md-1">{{
									content.planner_resumeOrder
								}}</span>
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, nextTick, onActivated, onBeforeUnmount, onMounted, onUpdated, ref, watch } from "vue";
import { useStore } from "vuex";
import Prices from "@/vue/planning/components/common/prices.vue"
import PricesPP from "@/vue/planning/components/common/pricesPP.vue"
import ParticipantsTotals from "@/vue/planning/components/common/participantsTotals.vue"
import { elementInViewport } from "@/utils/utils"
import pricesLoadingSet from "@/vue/planning/state/pricesLoadingSet"
import currentCacheKey from "@/vue/planning/computed/currentCacheKey"
import getReservationClickData from "@/tracking/logic/getReservationClickData"
import getSiteFromDomain from "@/vue/planning/logic/getSiteFromDomain"

// Tracking
import trackReservationClick from "@/tracking/trackReservationClick"

const store = useStore();

const stickySummary = ref<HTMLElement | null>(null);
const speed = 250;

let scrollListener: (() => void) | null = null;
let throttleTimer: ReturnType<typeof window.setTimeout> | null = null;
const isListenerActive = ref(false);

// if there is no product available, don't show the summary
const hideStickySummary = computed(() => {
	return Object.keys(store.getters.wiypCall).length === 0;
});

const content = computed(() => store.state.content);
const participants = computed(() => store.state.participants);

const totalSelectedHebergements = computed<number>(() => store.getters.totalSelectedHebergements);
const totals = computed(() => store.getters.totals);
const selectedPrestations = computed(() => store.getters.selectedPrestations);
const locale = computed(() => store.getters.locale);

const checkIfPerPersonDisplayWebsite = computed(() => {
	return ["www.uk.travelski.com","www.nl.travelski.com", "www.benl.yoonly.eu"].includes(getSiteFromDomain(window.location.hostname));
});

function fadeIn(display = "block") {
	const el = stickySummary.value;
	if (!el) return;
	el.style.opacity = "0";
	el.style.display = display;
	nextTick(() => {
		const el2 = stickySummary.value;
		if (!el2) return;
		el2.style.opacity = "1";
	});
}

function fadeOut() {
	const el = stickySummary.value;
	if (!el) return;

	el.style.opacity = "0";
	window.setTimeout(() => {
		const el2 = stickySummary.value;
		if (!el2) return;
		el2.style.display = "none";
	}, speed);
}

function handleReserveClick() {
	store.dispatch("reserveClick");

	const {
		quantity,
		liheId,
		liheName,
		duration,
		beginDate,
		endDate,
		prestationsIds,
		prestationsNames,
		totalPrice,
		station,
		region,
	} = getReservationClickData();

	const normalizedTotalPrice =
		typeof totalPrice === "string" ? Number(totalPrice) : totalPrice;

	trackReservationClick(
		"book",
		quantity,
		prestationsIds,
		prestationsNames,
		normalizedTotalPrice,
		beginDate,
		endDate,
		duration,
		liheId,
		liheName,
		station,
		region
	);
}

function cleanupScrollListener() {
	isListenerActive.value = false;

	if (scrollListener) {
		try {
			window.removeEventListener("scroll", scrollListener);
		} catch (e) {
			console.warn("[StickySummary] Error removing listener:", e);
		}
		scrollListener = null;
	}

	if (throttleTimer) {
		window.clearTimeout(throttleTimer);
		throttleTimer = null;
	}
}

function activateScrollListener() {
	// Remove existing listener if any to prevent duplicates
	cleanupScrollListener();

	const throttledScrollListener = () => {
		// Throttle: if timer is running, skip this event
		if (throttleTimer) return;

		const el = stickySummary.value;
		if (!el) return;

		const summary = document.querySelector("#summary");
		const footer = document.querySelector("footer");
		const summaryInView = !!summary && elementInViewport(summary as Element);
		const footerInView = !!footer && elementInViewport(footer as Element);

		if (summaryInView || footerInView) {
			if (el.style.display !== "none") fadeOut();
		} else {
			if (el.style.display === "none") {
				if (totalSelectedHebergements.value === 0) return;
				fadeIn();
			}
		}

		throttleTimer = window.setTimeout(() => {
			throttleTimer = null;
		}, 50);
	};

	scrollListener = throttledScrollListener;

	try {
		window.addEventListener("scroll", scrollListener, { passive: true });
		isListenerActive.value = true;
	} catch (e) {
		console.error("[StickySummary] Error activating scroll listener:", e);
		isListenerActive.value = false;
	}
}

function decideShowOrHideStickySummary(newValue: number, oldValue: number) {
	if (newValue > 0 && oldValue === 0) {
		// Always fadeIn, let scroll handler hide if needed
		fadeIn();
	} else if (newValue === 0 && oldValue > 0) {
		fadeOut();
	}
}

watch(totalSelectedHebergements, (newValue, oldValue) => {
	decideShowOrHideStickySummary(newValue, oldValue);
});

onMounted(() => {
	if (totalSelectedHebergements.value) fadeIn();
	nextTick(() => {
		activateScrollListener();
	});
});

onUpdated(() => {
	if (stickySummary.value && (!isListenerActive.value || !scrollListener)) {
		nextTick(() => {
			if (!isListenerActive.value) activateScrollListener();
		});
	}
});

onActivated(() => {
	if (!isListenerActive.value) {
		nextTick(() => {
			activateScrollListener();
		});
	}
});

onBeforeUnmount(() => {
	cleanupScrollListener();
});
</script>

<style scoped>
#sticky-summary {
	position: fixed;
	left: 0px;
	bottom: 0px;
	width: 100%;
	z-index: 1000;
	transition: opacity 250ms ease-in-out;
}
</style>
